import React, { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Typography, Button } from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ROUTES from '@src/constants/route';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import {
  VOICE_STATUS,
  VOICE_PROCESSING,
  VOICE_COMPLETED,
  VOICE_NOT_USED,
  TIME_EXPIRE_QR_VOICE_CLONING,
} from '@src/constants/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import {
  PACKAGE_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import apis from '@src/apis';
import {
  AIVOICE_TTS_APP_STORE_URL,
  AIVOICE_TTS_DEEPLINK,
  AIVOICE_TTS_GOOGLE_PLAY_URL,
} from '@src/constants/mobileApp';
import CrownImg from '@src/assets/images/crown.png';
import ExclamationMark from '@src/assets/images/warning.png';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import Tooltip from '@src/components/Tooltip';
import useUserInfo from '@src/hooks/useTtsUser';
import { StyledHome } from './index.style';
import VoicesInformation from './VoicesInformation';
import QRVoiceNewDialog from '../QRDialog';

const Home = ({ voicesArray, setTotal, totalVoices = 0 }) => {
  const { keycloak } = useCustomKeycloak();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isCloseProcessingVoice, setIsCloseProcessingVoice] = useState(false);
  const [isCloseCompletedVoice, setIsCloseCompletedVoice] = useState(false);
  const [isCloseNotUsedVoice, setIsCloseNotUsedVoice] = useState(false);
  const [openQRVoiceNewDialog, setOpenQRVoiceNewDialog] = useState(false);
  const [voices, setVoices] = useState(voicesArray);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrExpireDate, setQrExpireDate] = useState('');
  const [qrRedirectUrl, setQRRedirectUrl] = useState('');
  const [
    showPopupLimitNumberVoiceCloning,
    setShowPopupLimitNumberVoiceCloning,
  ] = useState(false);
  const [showButtonUpgrade, setShowButtonUpgrade] = useState(false);
  const [redirectPaymentUrl, setRedirectPaymentUrl] = useState('');

  const [packageVipYear, setPackageVipYear] = useState(null);

  const { usingPackage } = useSelector((state) => state.user);

  const processingVoices = voices.filter((item) =>
    VOICE_PROCESSING.includes(item.status),
  );
  const completedVoices = voices.filter((item) =>
    VOICE_COMPLETED.includes(item.status),
  );
  const notUsedVoices = voices.filter((item) =>
    VOICE_NOT_USED.includes(item.status),
  );

  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();

  const { getFeatureValue } = useFeatureFlags();
  const allowCreateCloningVoice = getFeatureValue(
    FEATURE_KEYS.ALLOW_CREATE_CLONING_VOICE,
    {
      userId: user?.id,
      email: user?.email,
      phone: user?.phone,
    },
  );

  const isExpiredPackage =
    !ttsUser?.packageExpiryDate || moment().isAfter(ttsUser?.packageExpiryDate);

  const checkUserCanUseVoiceCloning =
    getFeatureValue(FEATURE_KEYS.VOICE_CLONING, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    }) ||
    (validateUserPackageCanUseVoiceCloning(ttsUser) && !isExpiredPackage);

  const isNewQRFlow = getFeatureValue(FEATURE_KEYS.NEW_QR_FLOW, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const useLimitVoiceCloning = getFeatureValue(
    FEATURE_KEYS.LIMIT_CREATE_VOICE_CLONING,
    { userId: user?.id },
  );

  const handleCloseProcessingVoices = () => {
    setIsCloseProcessingVoice(!isCloseProcessingVoice);
  };

  const handleCloseCompletedVoices = () => {
    setIsCloseCompletedVoice(!isCloseCompletedVoice);
  };

  const handleCloseVoicesNotUsed = () => {
    setIsCloseNotUsedVoice(!isCloseNotUsedVoice);
  };

  const handleClickContinueRecordInMobile = (voiceId) =>
    history.push(`${ROUTES.VOICE_CLONING_VOICES}/${voiceId}`);

  const hasVoiceRecording = voices.find(
    (item) =>
      item.status === VOICE_STATUS.RECORDING ||
      item.status === VOICE_STATUS.REJECTED,
  );

  const generateQRCodeUrl = async (loginCode) => {
    if (isNewQRFlow) {
      const screenApp = 'voiceCloning';
      return `${LANDING_PAGE_URL}/apps/aivoice-tts?screen=${screenApp}&loginCode=${loginCode}`;
    }

    let response;
    try {
      response = await apis.shortlink.generateShortlink({
        webAppUrl: `${CRM_URL}/studio/home?loginCode=${loginCode}`,
        deeplink: `${AIVOICE_TTS_DEEPLINK.LOGIN}?loginCode=${loginCode}`,
        appStoreUrl: AIVOICE_TTS_APP_STORE_URL,
        googlePlayUrl: AIVOICE_TTS_GOOGLE_PLAY_URL,
        ttl: TIME_EXPIRE_QR_VOICE_CLONING,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('errorGenerateQRCode', error);
    }
    if (!response?.status || !response?.result?.shortlink) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return null;
    }
    return response?.result?.shortlink;
  };

  const handleOpenQR = async (redirectUrl) => {
    setQRRedirectUrl(redirectUrl);

    const loginCode = await keycloak.generateLoginCode(redirectUrl);
    if (!loginCode) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return;
    }
    const url = await generateQRCodeUrl(loginCode);
    if (!url) return;

    setQrExpireDate(moment().add(TIME_EXPIRE_QR_VOICE_CLONING, 'milliseconds'));
    setQrCodeUrl(url);
  };

  const handleOpenQRVoiceRecording = (voiceRecordingId) => {
    const voice = voices.find((item) => item.id === voiceRecordingId);
    dispatch(actions.voiceVoiceCloning.addVoice(voice));
    const redirectUrl = `${CRM_URL}${ROUTES.VOICE_CLONING_VOICES}/${voiceRecordingId}`;
    if (checkUserCanUseVoiceCloning) handleOpenQR(redirectUrl);
    setOpenQRVoiceNewDialog(true);
  };

  const handleShowDialogUpgradePackage = () => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('voiceCloningUpgrade'),
        img: CrownImg,
        classNameImg: 'custom-img',
        showButtonUnderstand: true,
        onUpgrade: () => {
          history.push(redirectPaymentUrl);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );

    return null;
  };

  const handleShowPopupLimitNumberVoiceCloning = (isVipPackage) => {
    setShowPopupLimitNumberVoiceCloning(true);
    setShowButtonUpgrade(!isVipPackage);
  };

  const handleContinueRecord = (voiceRecordingId) => {
    if (!checkUserCanUseVoiceCloning) {
      handleShowDialogUpgradePackage();
      return;
    }
    const hasRecording = !!hasVoiceRecording;
    if (hasRecording) {
      if (isMobile) {
        handleClickContinueRecordInMobile(voiceRecordingId);
        return;
      }
      handleOpenQRVoiceRecording(voiceRecordingId);
    }
  };

  const isValidNumberOfVoicesCanUse = useMemo(() => {
    if (
      ttsUser &&
      useLimitVoiceCloning &&
      totalVoices !== null &&
      totalVoices !== undefined
    ) {
      const { maxCloningVoices = 0 } = ttsUser;
      if (totalVoices >= maxCloningVoices) return false;
    }

    return true;
  }, [totalVoices, ttsUser, useLimitVoiceCloning]);

  const handleValidateNumberClonedVoice = () => {
    const isVipPackage = usingPackage && usingPackage?.code === packageVipYear;
    handleShowPopupLimitNumberVoiceCloning(isVipPackage);
    return null;
  };

  const handleClickGenerateVoice = async () => {
    if (!checkUserCanUseVoiceCloning) {
      handleShowDialogUpgradePackage();
      return;
    }

    if (!isValidNumberOfVoicesCanUse) {
      handleValidateNumberClonedVoice();
      return;
    }

    const redirectUrl = `${CRM_URL}${ROUTES.VOICE_CLONING_CREATE}`;
    await handleOpenQR(redirectUrl);
    setOpenQRVoiceNewDialog(true);
  };

  const handleVoiceInfoChange = (id, newValues) => {
    const updatedVoices = voices.map((voice) =>
      voice.id === id ? { ...voice, ...newValues } : voice,
    );
    setVoices(updatedVoices);

    const updatedVoice = updatedVoices.find((voice) => voice.id === id);
    if (updatedVoice)
      dispatch(actions.voiceVoiceCloning.addVoice(updatedVoice));
  };

  const handleDeleteVoice = (id) => {
    const updatedVoices = voices.filter((voice) => voice.id !== id);
    setVoices(updatedVoices);
    setTotal(updatedVoices.length);
  };

  const handleChangeVoiceData = (id, data) => {
    const updatedVoices = voices.map((voice) =>
      voice.id === id ? { ...voice, ...data } : voice,
    );
    setVoices(updatedVoices);
  };

  const handleFetchPackages = async () => {
    try {
      const res = await apis.packages.getPackages({
        active: true,
        purchasable: true,
        type: PACKAGE_TYPE.STUDIO,
      });
      if (res?.status) {
        const packageCodes = res?.result?.packages?.map((item) => item.code);
        const packageSpecialYearCode = packageCodes.find(
          (item) =>
            (item.includes(PACKAGE_LEVEL.SPECIAL) ||
              item.includes(PACKAGE_LEVEL.ENTERPRISE)) &&
            item.includes(PACKAGE_DURATION.YEAR),
        );
        const packageVipYearCode = packageCodes.find(
          (item) =>
            item.includes(PACKAGE_LEVEL.VIP) &&
            item.includes(PACKAGE_DURATION.YEAR),
        );

        setPackageVipYear(packageVipYearCode);
        setRedirectPaymentUrl(
          `${ROUTES.PAYMENT}?package=${packageSpecialYearCode}`,
        );
      }
    } catch (error) {
      setRedirectPaymentUrl(ROUTES.PAYMENT);
    }
  };

  const renderActionComponent = () => {
    if (showButtonUpgrade) {
      return (
        <>
          <Button
            className="outlined-button"
            onClick={() => setShowPopupLimitNumberVoiceCloning(false)}
          >
            {t('gotItV2')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              history.push(redirectPaymentUrl);
              setShowPopupLimitNumberVoiceCloning(false);
            }}
          >
            {t('upgradeNow')}
          </Button>
        </>
      );
    }

    return (
      <Button
        variant="contained"
        onClick={() => setShowPopupLimitNumberVoiceCloning(false)}
      >
        {t('gotItV2')}
      </Button>
    );
  };

  useEffect(() => {
    handleFetchPackages();
  }, []);

  return (
    <>
      {voices.length > 0 && (
        <StyledHome>
          <Box className="box-voices">
            <Box className="box-home-title">
              <Typography className="home-title">{t('voiceList')}</Typography>
              {allowCreateCloningVoice && (
                <Button
                  color="primary"
                  variant="contained"
                  className="generate-btn"
                  onClick={handleClickGenerateVoice}
                >
                  <AddRounded className="add-icon" />
                </Button>
              )}
              {useLimitVoiceCloning && (
                <Box className="number-voice-container">
                  <Tooltip
                    arrow
                    className="tootlip"
                    placement="left"
                    title={t('voiceCloningLimitTooltipDesc', {
                      number: ttsUser?.maxCloningVoices,
                    })}
                  >
                    <InfoRoundedIcon className="info-icon" />
                  </Tooltip>
                  <Typography className="number-voice">
                    {`${t('voiceCloningCount')}: `}
                    <span className="span-number-voice">{`${totalVoices}/${
                      ttsUser?.maxCloningVoices || 0
                    }`}</span>
                  </Typography>
                </Box>
              )}
            </Box>
            {!!processingVoices?.length && (
              <VoicesInformation
                boxVoicesName={t('process')}
                closeVoices={isCloseProcessingVoice}
                onCloseVoices={handleCloseProcessingVoices}
                voices={processingVoices}
                checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                onContinueRecord={handleContinueRecord}
                onVoiceInfoChange={handleVoiceInfoChange}
                onDeleteVoice={handleDeleteVoice}
                handleChangeVoiceData={handleChangeVoiceData}
                handleClickGenerateVoice={handleClickGenerateVoice}
                onShowUpgradePackage={handleShowDialogUpgradePackage}
                onShowPopupLimitNumberVoiceCloning={
                  handleValidateNumberClonedVoice
                }
              />
            )}
            {!!completedVoices?.length && (
              <VoicesInformation
                boxVoicesName={t('isCompleted')}
                closeVoices={isCloseCompletedVoice}
                checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                onCloseVoices={handleCloseCompletedVoices}
                voices={completedVoices}
                isVoiceStatusCompleted
                onVoiceInfoChange={handleVoiceInfoChange}
                onContinueRecord={handleContinueRecord}
                onDeleteVoice={handleDeleteVoice}
                handleChangeVoiceData={handleChangeVoiceData}
                handleClickGenerateVoice={handleClickGenerateVoice}
                onShowUpgradePackage={handleShowDialogUpgradePackage}
                onShowPopupLimitNumberVoiceCloning={
                  handleValidateNumberClonedVoice
                }
              />
            )}
            {!!notUsedVoices?.length && (
              <VoicesInformation
                boxVoicesName={t('notUsed')}
                closeVoices={isCloseNotUsedVoice}
                onCloseVoices={handleCloseVoicesNotUsed}
                voices={notUsedVoices}
                onVoiceInfoChange={handleVoiceInfoChange}
                onContinueRecord={handleContinueRecord}
                onDeleteVoice={handleDeleteVoice}
                handleChangeVoiceData={handleChangeVoiceData}
                handleClickGenerateVoice={handleClickGenerateVoice}
                onShowUpgradePackage={handleShowDialogUpgradePackage}
                checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                onShowPopupLimitNumberVoiceCloning={
                  handleValidateNumberClonedVoice
                }
              />
            )}
          </Box>
          <QRVoiceNewDialog
            urlToShow={qrCodeUrl}
            open={openQRVoiceNewDialog}
            qrExpireDate={qrExpireDate}
            qrRedirectUrl={qrRedirectUrl}
            onClose={() => setOpenQRVoiceNewDialog(false)}
            refreshQRCode={handleOpenQR}
          />
        </StyledHome>
      )}
      {showPopupLimitNumberVoiceCloning && (
        <NotificationDialogV4
          image={showButtonUpgrade ? CrownImg : ExclamationMark}
          title={t('voiceCloningLimitTitle')}
          description={
            showButtonUpgrade
              ? t('voiceCloningLimitDescWithUpgrade', {
                  numberVoices: ttsUser?.maxCloningVoices || 0,
                })
              : t('voiceCloningLimitDescWithNoUpgrade', {
                  numberVoices: ttsUser?.maxCloningVoices || 0,
                })
          }
          actionComponent={renderActionComponent()}
          open={showPopupLimitNumberVoiceCloning}
          onClose={() => setShowPopupLimitNumberVoiceCloning(false)}
        />
      )}
    </>
  );
};

export default Home;
